import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { AmountFilterOperation } from 'constants/common';
import { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import MoreFilters, {
  AppliedFilter,
  DateFilter,
  FilterDropdownType,
  FilterOption
} from 'components/more-filters/MoreFilters';
import CustomAmountFilter from 'components/custom-amount-filter/CustomAmountFilter';
import DropdownWithSearch from 'components/dropdown-with-search/DropdownWithSearch';
import { CARD_ON_FILE_OPTIONS, CUSTOMER_MORE_FILTER_OPTIONS, CustomerFilterIds } from '../constants';
import { getAmountQueryParam, getTextForFilter, reverseAmountQueryParam } from 'containers/dispatch/utils';
import { format } from 'date-fns';
import CustomDateRangePicker from 'components/react-date-range-picker/ReactDateRangePicker';
import { getCustomerDateDefaultRange } from '../Utils';
import { useLazyGetMerchantsByTeamIdReceiptsQuery } from 'containers/receipts/api';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { selectCurrentCustomerView } from '../customersSlice';
import DateUtils from 'utils/dateUtils';

interface CustomerFiltersProps {
  onFiltersChange: (filters: any) => void;
}

interface CustomerFilters {
  lastActivity: string[];
  cardOnFile: string[];
  createdStartDate: string | null;
  createdEndDate: string | null;
  totalInvoicedFilter: AmountFilter;
  outstandingBalanceFilter: AmountFilter;
  numberOfJobsFilter: AmountFilter;
  totalPaidFilter: AmountFilter;
  disputeLossesFilter: AmountFilter;
  refundsFilter: AmountFilter;
  createdBy: string[];
}

const CustomerFilters: FC<CustomerFiltersProps> = memo(({ onFiltersChange }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentCustomerView = useAppSelector(selectCurrentCustomerView);

  const [cardOnFileCount, setCardOnFileCount] = useState<string[]>([]);
  const [draftCardOnFile, setDraftCardOnFile] = useState<string[]>([]);
  const [totalInvoicedFilter, setTotalInvoicedFilter] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });
  const [outstandingBalanceFilter, setOutstandingBalanceFilter] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });
  const [jobCount, setJobCount] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });
  const [totalPaidFilter, setTotalPaidFilter] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });
  const [disputeLossesFilter, setDisputeLossesFilter] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });
  const [refundsFilter, setRefundsFilter] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);

  const [lastActivityAtFrom, setLastActivityAtFrom] = useState<string | null>(null);
  const [lastActivityAtTo, setLastActivityAtTo] = useState<string | null>(null);
  const [createdAtFrom, setCreatedAtFrom] = useState<string | null>(null);
  const [createdAtTo, setCreatedAtTo] = useState<string | null>(null);

  const [createdBy, setCreatedBy] = useState<string[]>([]);

  const [getMerchantsQuery, getMerchantsQueryState] = useLazyGetMerchantsByTeamIdReceiptsQuery();

  const { data: getMerchantsResponse } = getMerchantsQueryState;
  const merchantOptions = useMemo(() => getMerchantsResponse || [], [getMerchantsResponse]);

  useEffect(() => {
    if (currentTeam?.id) {
      getMerchantsQuery({ teamId: currentTeam.id });
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    const { filters } = currentCustomerView || {};
    const {
      lastActivityStartDate: lastActivityStartDateFilter,
      lastActivityEndDate: lastActivityEndDateFilter,
      cardOnFileCount: cardOnFileFilter,
      totalInvoicedAmount: totalInvoicedAmountFilter,
      outstandingBalanceAmount: outstandingBalanceAmountFilter,
      jobCount: jobCountFilter,
      totalPaidAmount: totalPaidAmountFilter,
      disputeLossAmount: disputeLossAmountFilter,
      refundAmount: refundAmountFilter,
      createdStartDate: createdStartDateFilter,
      createdEndDate: createdEndDateFilter,
      createdBy: createdByFilter
    } = filters || {};
    if (lastActivityStartDateFilter) {
      setLastActivityAtFrom(lastActivityStartDateFilter as string);
    } else {
      setLastActivityAtFrom(null);
    }
    if (lastActivityEndDateFilter) {
      setLastActivityAtTo(lastActivityEndDateFilter as string);
    } else {
      setLastActivityAtTo(null);
    }
    if (cardOnFileFilter) {
      setCardOnFileCount([cardOnFileFilter] as string[]);
    } else {
      setCardOnFileCount([]);
    }
    if (totalInvoicedAmountFilter) {
      const totalInvoicedFilterToSet =
        typeof totalInvoicedAmountFilter === 'string'
          ? reverseAmountQueryParam(totalInvoicedAmountFilter)
          : totalInvoicedAmountFilter;
      setTotalInvoicedFilter(totalInvoicedFilterToSet as AmountFilter);
    } else {
      setTotalInvoicedFilter({
        filterOperation: AmountFilterOperation.ALL,
        minAmount: null,
        maxAmount: null
      });
    }
    if (outstandingBalanceAmountFilter) {
      const outstandingBalanceFilterToSet =
        typeof outstandingBalanceAmountFilter === 'string'
          ? reverseAmountQueryParam(outstandingBalanceAmountFilter)
          : outstandingBalanceAmountFilter;
      setOutstandingBalanceFilter(outstandingBalanceFilterToSet as AmountFilter);
    } else {
      setOutstandingBalanceFilter({
        filterOperation: AmountFilterOperation.ALL,
        minAmount: null,
        maxAmount: null
      });
    }
    if (jobCountFilter) {
      const jobCountToSet =
        typeof jobCountFilter === 'string' ? reverseAmountQueryParam(jobCountFilter) : jobCountFilter;
      setJobCount(jobCountToSet as AmountFilter);
    } else {
      setJobCount({
        filterOperation: AmountFilterOperation.ALL,
        minAmount: null,
        maxAmount: null
      });
    }
    if (totalPaidAmountFilter) {
      const totalPaidFilterToSet =
        typeof totalPaidAmountFilter === 'string'
          ? reverseAmountQueryParam(totalPaidAmountFilter)
          : totalPaidAmountFilter;
      setTotalPaidFilter(totalPaidFilterToSet as AmountFilter);
    } else {
      setTotalPaidFilter({
        filterOperation: AmountFilterOperation.ALL,
        minAmount: null,
        maxAmount: null
      });
    }
    if (disputeLossAmountFilter) {
      const disputeLossesFilterToSet =
        typeof disputeLossAmountFilter === 'string'
          ? reverseAmountQueryParam(disputeLossAmountFilter)
          : disputeLossAmountFilter;
      setDisputeLossesFilter(disputeLossesFilterToSet as AmountFilter);
    } else {
      setDisputeLossesFilter({
        filterOperation: AmountFilterOperation.ALL,
        minAmount: null,
        maxAmount: null
      });
    }
    if (refundAmountFilter) {
      const refundsFilterToSet =
        typeof refundAmountFilter === 'string' ? reverseAmountQueryParam(refundAmountFilter) : refundAmountFilter;
      setRefundsFilter(refundsFilterToSet as AmountFilter);
    } else {
      setRefundsFilter({
        filterOperation: AmountFilterOperation.ALL,
        minAmount: null,
        maxAmount: null
      });
    }
    if (createdStartDateFilter) {
      setCreatedAtFrom(
        DateUtils.getDateInFormat({
          date: new Date(createdStartDateFilter as string),
          dateFormat: 'yyyy-MM-dd'
        })
      );
    } else {
      setCreatedAtFrom(null);
    }
    if (createdEndDateFilter) {
      setCreatedAtTo(
        DateUtils.getDateInFormat({
          date: new Date(createdEndDateFilter as string),
          dateFormat: 'yyyy-MM-dd'
        })
      );
    } else {
      setCreatedAtTo(null);
    }
    if ((createdByFilter as string[])?.length > 0) {
      setCreatedBy(createdByFilter as string[]);
    } else {
      setCreatedBy([]);
    }
  }, [currentCustomerView]);

  const getCustomerFilterData = useCallback(
    (filterId: string) => {
      switch (filterId) {
        case CustomerFilterIds.CREATED_BY:
          return merchantOptions;
        case CustomerFilterIds.CREATED_DATE:
          return getCustomerDateDefaultRange();
      }
    },
    [merchantOptions]
  );

  useEffect(() => {
    onFiltersChange({
      lastActivityAtFrom: DateUtils.ensureUTCStartDateFormat(lastActivityAtFrom),
      lastActivityAtTo: DateUtils.ensureUTCEndDateFormat(lastActivityAtTo),
      cardOnFileCount: cardOnFileCount?.length > 0 ? cardOnFileCount.join(',') : undefined,
      totalInvoicedAmount: getAmountQueryParam(totalInvoicedFilter) || undefined,
      outstandingBalanceAmount: getAmountQueryParam(outstandingBalanceFilter) || undefined,
      jobCount: getAmountQueryParam(jobCount) || undefined,
      totalPaidAmount: getAmountQueryParam(totalPaidFilter) || undefined,
      disputeLossAmount: getAmountQueryParam(disputeLossesFilter) || undefined,
      refundAmount: getAmountQueryParam(refundsFilter) || undefined,
      createdAtFrom: DateUtils.ensureUTCStartDateFormat(createdAtFrom),
      createdAtTo: DateUtils.ensureUTCEndDateFormat(createdAtTo),
      createdBy: createdBy.length > 0 ? createdBy : undefined
    });
  }, [
    lastActivityAtFrom,
    lastActivityAtTo,
    cardOnFileCount,
    totalInvoicedFilter,
    outstandingBalanceFilter,
    jobCount,
    totalPaidFilter,
    disputeLossesFilter,
    refundsFilter,
    createdAtFrom,
    createdAtTo,
    createdBy
  ]);

  const handleLastActivityDateRange = (startDate: string | null, endDate: string | null) => {
    if (!startDate && !endDate) {
      setLastActivityAtFrom(null);
      setLastActivityAtTo(null);
    } else {
      setLastActivityAtFrom(startDate ? startDate : null);
      setLastActivityAtTo(endDate ? endDate : null);
    }
  };

  const getLastActivityText = () => {
    if (lastActivityAtFrom && lastActivityAtTo) {
      return `${format(new Date(lastActivityAtFrom), 'MMM d, yyyy')} - ${format(
        new Date(lastActivityAtTo),
        'MMM d, yyyy'
      )}`;
    }
    return '';
  };

  const handleApplyFilterFromMoreFilters = useCallback(
    (filterData: { selectedFilterItems: string[] | AmountFilter; filter: FilterOption }) => {
      const { selectedFilterItems, filter } = filterData;
      const updatedFilters = [...appliedFilters];
      const filterIndex = updatedFilters.findIndex(f => f.id === filter.id);

      let displayValue = '';
      if (filter.type === FilterDropdownType.AMOUNT) {
        const amountFilter = selectedFilterItems as AmountFilter;
        switch (filter.id) {
          case CustomerFilterIds.NUMBER_OF_JOBS:
            setJobCount(amountFilter);
            break;
          case CustomerFilterIds.TOTAL_PAID:
            setTotalPaidFilter(amountFilter);
            break;
          case CustomerFilterIds.DISPUTE_LOSSES:
            setDisputeLossesFilter(amountFilter);
            break;
          case CustomerFilterIds.REFUNDS:
            setRefundsFilter(amountFilter);
            break;
        }
        if (amountFilter.filterOperation === AmountFilterOperation.BW) {
          displayValue = `$${amountFilter.minAmount} - $${amountFilter.maxAmount}`;
        } else {
          displayValue = `$${amountFilter.minAmount}`;
        }
      } else if (filter.type === FilterDropdownType.DROPDOWN) {
        if (filter.id === CustomerFilterIds.CREATED_BY) {
          setCreatedBy(selectedFilterItems as string[]);
        }
        displayValue = getTextForFilter(
          merchantOptions,
          selectedFilterItems as string[],
          'merchantId',
          'firstName',
          'lastName'
        );
      } else if (filter.type === FilterDropdownType.DATE) {
        if (filter.id === CustomerFilterIds.CREATED_DATE) {
          const dateFilter = selectedFilterItems as unknown as DateFilter;
          setCreatedAtFrom(dateFilter.startDate);
          setCreatedAtTo(dateFilter.endDate);
          if (dateFilter.startDate && dateFilter.endDate) {
            displayValue = `${format(new Date(dateFilter.startDate), 'MMM d, yyyy')} - ${format(
              new Date(dateFilter.endDate),
              'MMM d, yyyy'
            )}`;
          }
        }
      }

      if (filterIndex !== -1) {
        updatedFilters[filterIndex] = {
          ...filter,
          value: selectedFilterItems,
          displayValue
        };
      } else {
        updatedFilters.push({
          ...filter,
          value: selectedFilterItems,
          displayValue
        });
      }

      setAppliedFilters(updatedFilters);
    },
    [appliedFilters, merchantOptions]
  );

  const handleRemoveFilter = useCallback((filterId: string) => {
    switch (filterId) {
      case CustomerFilterIds.NUMBER_OF_JOBS:
        setJobCount({
          filterOperation: AmountFilterOperation.ALL,
          minAmount: null,
          maxAmount: null
        });
        break;
      case CustomerFilterIds.TOTAL_PAID:
        setTotalPaidFilter({
          filterOperation: AmountFilterOperation.ALL,
          minAmount: null,
          maxAmount: null
        });
        break;
      case CustomerFilterIds.DISPUTE_LOSSES:
        setDisputeLossesFilter({
          filterOperation: AmountFilterOperation.ALL,
          minAmount: null,
          maxAmount: null
        });
        break;
      case CustomerFilterIds.REFUNDS:
        setRefundsFilter({
          filterOperation: AmountFilterOperation.ALL,
          minAmount: null,
          maxAmount: null
        });
        break;
      case CustomerFilterIds.CREATED_BY:
        setCreatedBy([]);
        break;
      case CustomerFilterIds.CREATED_DATE:
        setCreatedAtFrom(null);
        setCreatedAtTo(null);
        break;
    }
    setAppliedFilters(prev => prev.filter(filter => filter.id !== filterId));
  }, []);

  return (
    <div className="flex h-full w-full flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:gap-2">
      <CustomAmountFilter
        amountFilter={totalInvoicedFilter}
        setAmountFilter={setTotalInvoicedFilter}
        title="Total invoiced"
        isAmountFilter={true}
      />

      <CustomAmountFilter
        amountFilter={outstandingBalanceFilter}
        setAmountFilter={setOutstandingBalanceFilter}
        title="Outstanding balance"
      />

      <DropdownWithSearch
        data={CARD_ON_FILE_OPTIONS}
        dataKey="label"
        dataIdKey="value"
        selectedDraftArray={draftCardOnFile}
        setSelectedDraftArray={setDraftCardOnFile}
        selectedArray={cardOnFileCount}
        setSelectedArray={setCardOnFileCount}
        buttonPrimaryText="Card on file"
        filterPlaceholderText="Filter by card on file"
        allowSearch={false}
        showResultsCount={false}
        customClassName="m-0"
        filterId="card-on-file-filter"
        buttonSecondaryText={getTextForFilter(CARD_ON_FILE_OPTIONS, cardOnFileCount, 'value', 'label')}
      />

      <CustomDateRangePicker
        startDate={lastActivityAtFrom}
        setStartDate={setLastActivityAtFrom}
        endDate={lastActivityAtTo}
        setEndDate={setLastActivityAtTo}
        filterId="last-activity-filter"
        allowFutureDate={false}
        customRanges={getCustomerDateDefaultRange()}
        hasDefaultRanges={false}
        setDateRange={handleLastActivityDateRange}
        placeholder="Last activity"
        customSecondaryText={getLastActivityText()}
        onClear={() => {
          setLastActivityAtFrom(null);
          setLastActivityAtTo(null);
        }}
      />

      <MoreFilters
        onFilterApply={handleApplyFilterFromMoreFilters}
        onFilterRemove={handleRemoveFilter}
        appliedFilters={appliedFilters}
        filterOptions={CUSTOMER_MORE_FILTER_OPTIONS}
        getFilterData={getCustomerFilterData}
      />
    </div>
  );
});

CustomerFilters.displayName = 'CustomerFilters';

export default CustomerFilters;
